import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, StyleProp, ViewStyle, View, Text, TextStyle } from 'react-native';
import { Calendar } from 'react-native-calendars';
import { MarkingProps } from 'react-native-calendars/src/calendar/day/marking';
import { DateData, Direction, Theme } from 'react-native-calendars/src/types';
import XDate from 'xdate';
import { format as formatDate, startOfToday } from 'date-fns';
import { mbApplyTransparency } from '@/utils/general/style-utils';
import { MB_SVG_ChevronLeft, MB_SVG_ChevronRight } from '@/components/general/svg-components';

// TODO: It might be worth getting rid of XDate.

interface MB_CalendarMarkedDates {
  [key: string]: MarkingProps;
}

const TODAY_TEXT_COLOR = '#00bbf2';

export interface MB_CalendarProps {
  /**
   * The style of the calendar.
   */
  calendarStyle?: StyleProp<ViewStyle>;

  /**
   * Color theme.
   */
  theme?: Theme;

  /**
   * Minimum date that can be selected, dates before minDate will be grayed out.
   */
  minDate?: Date;

  /**
   * Handler which gets executed on day press.
   */
  onDayChanged?: (date: DateData) => void;

  /**
   * Handler which gets executed on month change.
   */
  onMonthChanged?: (date: DateData) => void;

  /**
   * The date that will be selected when the calendar is initialized.
   *
   * If this nor the `initialSelectedMonth` is provided, the current date will be selected.
   */
  initialSelectedDate?: string;

  /**
   * The month that will be selected when the calendar is initialized.
   *
   * This is superseded by `initialSelectedDate`.
   *
   * If this nor the `initialSelectedDate` is provided, the current month will be selected.
   */
  initialSelectedMonth?: string;

  /**
   * Disable days by default.
   */
  disabledByDefault?: boolean;

  /**
   * Disable all touch events for disabled days. Can be overridden with `disableTouchEvent` in `markedDates`.
   */
  disableAllTouchEventsForDisabledDays?: boolean;

  /**
   * Collection of dates that have to be marked.
   */
  markedDates?: MB_CalendarMarkedDates;

  /**
   * Style for the selected date's container.
   */
  selectedDateContainerStyle?: StyleProp<ViewStyle>;

  /**
   * Style for the selected date's text.
   */
  selectedDateTextStyle?: StyleProp<TextStyle>;

  /**
   * Disable changing month when click on days of other months.
   */
  disableDayAndMonthChange?: boolean;

  /**
   * The color of the calendar header's arrows.
   */
  arrowColor?: string;

  /**
   * The color of the calendar header's arrows when disabled.
   */
  disabledArrowColor?: string;

  /**
   * The style of the calendar header's arrows container.
   */
  arrowContainerStyle?: StyleProp<ViewStyle>;

  /**
   * The style of the calendar header's text.
   */
  headerTextStyle?: StyleProp<TextStyle>;
}

const MB_Calendar = ({
  calendarStyle,
  theme,
  minDate,
  onDayChanged,
  onMonthChanged,
  initialSelectedDate,
  initialSelectedMonth,
  disabledByDefault,
  disableAllTouchEventsForDisabledDays,
  markedDates,
  selectedDateContainerStyle,
  selectedDateTextStyle,
  disableDayAndMonthChange,
  arrowColor,
  disabledArrowColor,
  arrowContainerStyle,
  headerTextStyle,
}: MB_CalendarProps) => {
  const [currentMonth, setCurrentMonth] = useState<XDate | undefined>();
  const [selectedDate, setSelectedDate] = useState<string | undefined>();

  useEffect(() => {
    if (initialSelectedDate && !selectedDate) {
      if (!initialSelectedMonth) {
        setCurrentMonth(new XDate(initialSelectedDate));
      }
      setSelectedDate(initialSelectedDate);
    } else if (initialSelectedMonth && !currentMonth) {
      setCurrentMonth(new XDate(initialSelectedMonth));
    } else if (!initialSelectedDate && !initialSelectedMonth && !currentMonth) {
      setCurrentMonth(new XDate(startOfToday()));
    }
  }, [currentMonth, initialSelectedDate, initialSelectedMonth, selectedDate]);

  const disableLeft = useMemo(() => {
    return minDate && currentMonth && new XDate(currentMonth).clearTime().setDate(1).diffMonths(new XDate(minDate).clearTime().setDate(1)) >= 0;
  }, [currentMonth, minDate]);

  const CalendarHeader = useMemo(
    () => (date?: XDate | undefined) => {
      return <Text style={headerTextStyle ?? styles.smallerText}>{date?.toString('MMMM yyyy')}</Text>;
    },
    [headerTextStyle]
  );

  const CalendarHeaderArrows = ({ direction }: { direction: Direction }) => {
    return (
      <View style={[styles.headerButton, arrowContainerStyle]}>
        {direction === 'left' ? (
          <MB_SVG_ChevronLeft color={disableLeft ? disabledArrowColor ?? '#717171' : arrowColor ?? '#FFFFFF'} size={16} />
        ) : (
          <MB_SVG_ChevronRight color={arrowColor ?? '#FFFFFF'} size={16} />
        )}
      </View>
    );
  };

  const allMarkedDates = useMemo(() => {
    const todayDateString = formatDate(startOfToday(), 'yyyy-MM-dd');

    const marked: MB_CalendarMarkedDates = markedDates ?? {};
    if (selectedDate) {
      marked[selectedDate] = {
        ...marked?.[selectedDate],
        customStyles: {
          ...marked?.[selectedDate]?.customStyles,
          container: StyleSheet.flatten([styles.selectedDayContainerStyle, marked?.[selectedDate]?.customStyles?.container, selectedDateContainerStyle]),
          text: StyleSheet.flatten([marked?.[selectedDate]?.customStyles?.text, selectedDateTextStyle]),
        },
      };
    }

    const todayMarkedCustomStylesContainerFlattened = StyleSheet.flatten(marked[todayDateString]?.customStyles?.container ?? {});
    const todayMarkedCustomStylesTextFlattened = StyleSheet.flatten(marked[todayDateString]?.customStyles?.text ?? {});
    marked[todayDateString] = {
      ...marked?.[todayDateString],
      customStyles: {
        ...marked[todayDateString]?.customStyles,
        container: StyleSheet.flatten([
          todayMarkedCustomStylesContainerFlattened,
          {
            backgroundColor: theme?.todayBackgroundColor ?? todayMarkedCustomStylesContainerFlattened.backgroundColor ?? 'transparent',
          },
        ]),
        text: StyleSheet.flatten([
          todayMarkedCustomStylesTextFlattened,
          {
            color: theme?.todayTextColor ?? TODAY_TEXT_COLOR,
          },
        ]),
      },
    };

    return marked;
  }, [markedDates, selectedDate, selectedDateContainerStyle, selectedDateTextStyle, theme?.todayBackgroundColor, theme?.todayTextColor]);

  const onDayPressCallback = useCallback(
    (date: DateData) => {
      if (!disableDayAndMonthChange) {
        setSelectedDate(date.dateString);
        onDayChanged?.(date);
      }
    },
    [disableDayAndMonthChange, onDayChanged]
  );

  const onMonthChangedCallback = useCallback(
    (month: DateData) => {
      if (!disableDayAndMonthChange) {
        setCurrentMonth(new XDate(month.dateString));
        onMonthChanged?.(month);
      }
    },
    [disableDayAndMonthChange, onMonthChanged]
  );

  return (
    <Calendar
      disabledByDefault={disabledByDefault}
      disableAllTouchEventsForDisabledDays={disableAllTouchEventsForDisabledDays}
      markingType={'custom'}
      initialDate={currentMonth ? formatDate(currentMonth?.getTime(), 'yyyy-MM-dd') : undefined}
      style={[styles.calendarStyle, calendarStyle]}
      disableArrowLeft={disableLeft || disableDayAndMonthChange}
      disableArrowRight={disableDayAndMonthChange}
      disableMonthChange={disableDayAndMonthChange}
      minDate={minDate ? new XDate(minDate).toString('yyyy-MM-dd') : undefined}
      renderArrow={direction => <CalendarHeaderArrows direction={direction} />}
      renderHeader={CalendarHeader}
      onMonthChange={onMonthChangedCallback}
      markedDates={allMarkedDates}
      onDayPress={onDayPressCallback}
      theme={{
        calendarBackground: 'transparent',
        dayTextColor: mbApplyTransparency('#BAC0CA', 0.6),
        textDayFontSize: 12,
        selectedDayTextColor: '#FFFFFF',
        textDisabledColor: '#717171',
        todayTextColor: TODAY_TEXT_COLOR,
        ...theme,
      }}
      hideExtraDays
    />
  );
};

export { MB_Calendar };

const styles = StyleSheet.create({
  calendarStyle: {
    width: 350,
  },
  headerStyle: {
    backgroundColor: 'transparent',
    height: 44,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerButton: {
    width: 30,
    height: 30,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: mbApplyTransparency('#FFFFFF', 0.1),
    borderRadius: 4,
  },
  selectedDayContainerStyle: {
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    backgroundColor: '#9A4C95',
  },
  smallerText: {
    fontWeight: 'normal',
    fontSize: 12,
    lineHeight: 18,
    color: '#FFFFFF',
    textAlign: 'center',
  },
});
